import { motion, useReducedMotion } from 'framer-motion'
import { Sprout, TrendingDown, TrendingUp, UserCheck } from 'lucide-react'
import { CaptacaoLeadsHeroCard } from '@/components/Dashboard/CaptacaoLeadsHeroCard'
import {
  OPERACAO_METRIC_ACCENTS,
  OperacaoMetricCard,
} from '@/components/Dashboard/OperacaoMetricCard'
import { getDashboardVariants } from '@/components/Dashboard/dashboardMotion'
import {
  formatCaptacaoConversionRate,
  type CaptacaoTabView,
} from '@/components/Dashboard/captacaoTab.utils'
import { formatDashboardNumber } from '@/utils/crmValuesDisplay'
import { seriesHasVariation, trendChangePercent } from '@/utils/kpiTrend'

type Props = {
  view: CaptacaoTabView
  valuesVisible?: boolean
}

export function CaptacaoKpiGrid({ view, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const v = getDashboardVariants(reduced)
  const conversion = formatCaptacaoConversionRate(view.conversionRate, view.totalLeads, valuesVisible)
  const trendSeries = view.leadsCreatedByMonth.map((row) => row.count)
  const trendDelta = trendChangePercent(trendSeries)

  const secondaryCards = [
    {
      key: 'qualified',
      label: 'Qualificados',
      value: formatDashboardNumber(view.qualifiedLeads, valuesVisible),
      numericValue: view.qualifiedLeads,
      valueFormat: 'integer' as const,
      subtitle: 'Prontos para avançar no funil',
      trendPositive: view.qualifiedLeads > 0,
      icon: <UserCheck size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.closed,
    },
    {
      key: 'conversion',
      label: 'Taxa de conversão',
      value: conversion.value,
      numericValue: view.conversionRate,
      valueFormat: 'percent' as const,
      subtitle: conversion.hint ?? 'Convertidos ÷ entradas',
      trendPositive: view.conversionRate >= 20,
      icon: <TrendingUp size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.winRate,
    },
    {
      key: 'nurturing',
      label: 'Nutrição',
      value: formatDashboardNumber(view.nurturingLeads, valuesVisible),
      numericValue: view.nurturingLeads,
      valueFormat: 'integer' as const,
      subtitle: 'Leads em cadência longa',
      trendPositive: view.nurturingLeads > 0,
      icon: <Sprout size={16} />,
      accent: { bg: '#ecfccb', text: '#65a30d', ring: '#bef264' },
    },
    {
      key: 'lost',
      label: 'Perdidos / descartados',
      value: formatDashboardNumber(view.lostLeads, valuesVisible),
      numericValue: view.lostLeads,
      valueFormat: 'integer' as const,
      subtitle: 'Perdido ou descartado',
      trendPositive: view.lostLeads === 0,
      icon: <TrendingDown size={16} />,
      accent: { bg: '#fee2e2', text: '#dc2626', ring: '#fca5a5' },
    },
  ]

  return (
    <motion.div
      variants={v.kpiGrid}
      initial='hidden'
      animate='show'
      className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:grid-rows-2'
    >
      <motion.div variants={v.kpiItem} className='sm:col-span-2 xl:row-span-2'>
        <CaptacaoLeadsHeroCard
          view={view}
          trendSeries={trendSeries}
          valuesVisible={valuesVisible}
        />
      </motion.div>

      {secondaryCards.map((kpi, index) => (
        <motion.div key={kpi.key} variants={v.kpiItem} className='min-h-[118px]'>
          <OperacaoMetricCard
            label={kpi.label}
            value={kpi.value}
            numericValue={kpi.numericValue}
            valueFormat={kpi.valueFormat}
            subtitle={kpi.subtitle}
            trendDelta={
              kpi.key === 'qualified' && seriesHasVariation(trendSeries) ? trendDelta : null
            }
            trendPositive={kpi.trendPositive}
            icon={kpi.icon}
            accent={kpi.accent}
            valuesVisible={valuesVisible}
            animationDelay={(index + 1) * 90}
            variant='compact'
          />
        </motion.div>
      ))}
    </motion.div>
  )
}
